import { env } from './env'
import {
  getAuthItem,
  setAuthItem,
  removeAuthItem,
  getRefreshTokenSecure,
} from './authStorage'
import { refreshSession } from './auth-refresh'
import { isNative } from './capacitor'
import { getSelectedWorkspaceId } from './api/workspace-context'
import { diag } from './diag'
import { qaRequestId } from './qa-telemetry'

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: HeadersInit
  searchParams?: Record<string, string>
  authToken?: string
  guestId?: string
  skipRetry?: boolean // Internal flag to prevent infinite retry loops
}

export class ApiError extends Error {
  readonly status: number
  readonly payload?: unknown

  constructor(message: string, status: number, payload?: unknown) {
    super(message)
    this.status = status
    this.payload = payload
  }
}

export class ContainerWarmingUpError extends Error {
  readonly retryAfter: number

  constructor(message: string, retryAfter: number = 3) {
    super(message)
    this.name = 'ContainerWarmingUpError'
    this.retryAfter = retryAfter
  }
}

/**
 * Creates a full URL by combining the appropriate base URL with a path.
 * Auth endpoints (/auth/*) go to the supervisor, others go to the API gateway.
 */
function createUrl(path: string, searchParams?: Record<string, string>): string {
  const isAuthEndpoint = path.startsWith('/auth') || path.startsWith('auth')
  const base = isAuthEndpoint ? env.authBaseUrl : env.apiBaseUrl

  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  const url = new URL(`${normalizedBase}${normalizedPath}`)

  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

/**
 * Callback registered by SessionProvider so that when apiClient refreshes a
 * token internally (on 401 retry), React state is updated too. Without this,
 * components keep sending the stale expired token on every render even though
 * the new token is already sitting in localStorage.
 */
let onTokenRefreshed: ((token: string) => void) | null = null
export function setTokenRefreshCallback(cb: (token: string) => void): void {
  onTokenRefreshed = cb
}

/**
 * Try to refresh the access token using the refresh token cookie.
 * Returns the new access token or null if refresh failed.
 *
 * IMPORTANT: This function is mutex-protected - if multiple calls happen
 * simultaneously (e.g., from StrictMode double-mounting), only ONE actual
 * network request will be made, and all callers will get the same result.
 */
async function tryRefreshToken(): Promise<string | null> {
  // The /auth/refresh network call + revoke-before-mint rotation now lives in the
  // shared single-flight core (lib/auth-refresh.ts), so this path and
  // SessionProvider's refreshAccessTokenWithStatus coalesce into ONE request and
  // can't race-revoke each other's Keychain refresh token (native-oauth-pass Ch.1
  // review fix). The apiClient-specific side effects (persist the access token to
  // storage + notify SessionProvider) stay here.
  const storedToken = typeof window !== 'undefined' ? getAuthItem('xavior_auth_token') : null
  const { accessToken, email, status } = await refreshSession(storedToken)

  if (!accessToken) {
    console.log('[apiClient] Refresh failed, status:', status)
    return null
  }

  if (typeof window !== 'undefined') {
    setAuthItem('xavior_auth_token', accessToken)
    if (email) {
      setAuthItem('userEmail', email)
    }
  }

  // Propagate to SessionProvider React state so components get the new token
  // without needing a page reload.
  onTokenRefreshed?.(accessToken)

  return accessToken
}

/**
 * Public API for refreshing the access token.
 * This is called by SessionProvider on initial load to restore sessions.
 */
export async function refreshAccessToken(): Promise<string | null> {
  return tryRefreshToken()
}

export async function apiFetch<T = unknown>(
  path: string,
  { searchParams, authToken, guestId, headers, skipRetry, ...init }: RequestOptions = {},
): Promise<T> {
  const url = createUrl(path, searchParams)

  const requestHeaders = new Headers(headers)

  requestHeaders.set('Accept', 'application/json')

  // QA-only per-attempt correlation id (joins this call to its API log line).
  // Built on `requestHeaders`, not the original `headers` the 401-retry re-passes,
  // so the retry mints a fresh id rather than reusing this attempt's.
  const qaRid = qaRequestId()
  if (qaRid) {
    requestHeaders.set('x-request-id', qaRid)
  }

  if (init.body && typeof init.body === 'string') {
    requestHeaders.set('Content-Type', 'application/json')
  }

  // Native (Capacitor iOS) logout must carry the Keychain refresh token so the
  // server can revoke it — there is no HttpOnly cookie to revoke (ITP). The UI
  // logout (WorkspaceOptionsModal) calls this with NO authToken, so attach the
  // header here, before the authToken block. sprint native-oauth-pass Ch.1.
  if ((path === '/auth/logout' || path === 'auth/logout') && isNative()) {
    requestHeaders.set('X-Client-Platform', 'capacitor')
    const refreshToken = await getRefreshTokenSecure()
    if (refreshToken) {
      requestHeaders.set('X-Refresh-Token', refreshToken)
    }
  }

  if (authToken) {
    requestHeaders.set('Authorization', `Bearer ${authToken}`)
    // Tag native (Capacitor iOS) requests so the backend treats this as a
    // header-refresh mobile client (sprint native-oauth-pass Ch.1). Web is
    // untouched (AC 6).
    if (isNative()) {
      requestHeaders.set('X-Client-Platform', 'capacitor')
    }
    if (typeof window !== 'undefined') {
      const storedEmail = getAuthItem('userEmail')
      if (storedEmail) {
        requestHeaders.set('X-User-Email', storedEmail)
      }
    }
    const workspaceId = getSelectedWorkspaceId()
    if (workspaceId) {
      requestHeaders.set('X-Workspace-Id', workspaceId)
    }
  } else if (guestId) {
    requestHeaders.set('X-Guest-Id', guestId)
  }

  const response = await fetch(url, {
    ...init,
    headers: requestHeaders,
    credentials: 'include', // CRITICAL: Send session cookies with every request
  })

  const contentType = response.headers.get('Content-Type') ?? ''
  const isJson = contentType.includes('application/json')

  let data: unknown = null

  if (isJson) {
    try {
      data = await response.json()
    } catch (error) {
      console.warn('apiFetch: failed to parse JSON', error)
    }
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    // Container warming up: 503 with the X-Container-Status header.
    if (response.status === 503 && response.headers.get('X-Container-Status') === 'warming-up') {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '3', 10)
      const message = typeof data === 'object' && data !== null && 'message' in data
        ? (data as { message: string }).message
        : 'Your secure container is starting up...'

      throw new ContainerWarmingUpError(message, retryAfter)
    }

    // #5105 loop diagnostics: a workspace-scoped 401/403 for a non-owner member
    // is the leading suspect for the entry loop — capture it (path + status, no
    // bodies/headers) so the reload-surviving buffer shows the cycle.
    if (response.status === 401 || response.status === 403) {
      diag('api', `${response.status} ${init.method ?? 'GET'} ${path}`, {
        wsId: getSelectedWorkspaceId(),
        skipRetry: Boolean(skipRetry),
      })
    }

    // 401: refresh the token and retry once (skipRetry guards against a loop).
    if (response.status === 401 && authToken && !skipRetry && path !== '/auth/refresh') {
      console.log('[apiClient] Got 401, attempting token refresh and retry...')
      const newToken = await tryRefreshToken()

      if (newToken) {
        console.log('[apiClient] Retrying request with new token')
        return apiFetch<T>(path, {
          ...init,
          searchParams,
          authToken: newToken,
          guestId,
          headers,
          skipRetry: true,
        })
      } else {
        console.log('[apiClient] Token refresh failed, clearing session')
        diag('api', `refresh failed after 401 on ${path} — clearing token`)
        if (typeof window !== 'undefined') {
          removeAuthItem('xavior_auth_token')
          removeAuthItem('userEmail')
          // Parity with lib/http.ts:287 (#5420) — without this, SessionProvider
          // never learns the session died and stays 'authenticated' while every
          // subsequent request 401s. `authToken` (checked above) confirms a
          // token was actually present before this clear, so a request that was
          // never authenticated in the first place can't spam this event.
          window.dispatchEvent(new CustomEvent('xavior:session-expired'))
        }
      }
    }

    throw new ApiError(
      `Request to ${path} failed with status ${response.status}`,
      response.status,
      data,
    )
  }

  return data as T
}

